import React, {useEffect, useState} from "react";
import {Descriptions, Popover, Tag} from "antd";
import dayjs from "dayjs";
import {Baecha} from "../../../types/Baecha";
import {formatKorDate} from "../../../util/date";
import {
	formatCurrency,
	formatPhoneNumber,
	getBaechaPriceReceiveMethodStr,
	getBaechaPriceTypeStr,
	getBaechaStatusStr,
	getStatusColor,
} from "../../../util/format";
import {BaechaWorkDay} from "../../../types/BaechaWorkDay";
import {CallPass} from "../../../types/CallPass";
import BaechaWorkDayDetail from "./BaechaWorkDayDetail";
import {BaechaUpdateHistory} from "../../../types/BaechaUpdateHistory";
import CallPassTable from "./CallPassTable";
import styles from "./BaechaListPanel.module.scss";
import userAPI from "../../../api/userAPI";
import {calcFeeAPI} from "../../../api/baechaAPI";

interface BaechaListPanelProps {
	baechaData: Baecha | undefined;
	callPassData: CallPass[];
	baechaWorkDayData: BaechaWorkDay[];
	baechaUpdateHistoryData: BaechaUpdateHistory[];
	onClickBaechaWorkDayItem: (baechaWorkDay: BaechaWorkDay, action: 'edit' | 'delete' | 'done') => void;
	onClickAddButton: () => void;
}

export const BaechaListPanel = ({
  baechaData,
  callPassData,
  baechaWorkDayData,
  baechaUpdateHistoryData,
  onClickBaechaWorkDayItem,
  onClickAddButton,
}: BaechaListPanelProps) : React.JSX.Element => {
	const [showBaljuUserPopup, setShowBaljuUserPopup] = useState(false);
	const [showSujuUserPopup, setShowSujuUserPopup] = useState(false);
	const [popupData, setPopupData] = useState<any>(undefined);
	const [fee, setFee] = useState(baechaData?.fee || 0);

	useEffect(() => {
		if (baechaData?.id && baechaWorkDayData.length > 0) {
			calcFeeAPI(baechaData?.id, baechaWorkDayData)
				.then((res) => {
					console.log(res);
					setFee(res);
				})
		} else {
			setFee(0);
		}
	}, [baechaData, baechaWorkDayData]);

	const onOpenPopup = async (bool: boolean, type: 'balju' | 'suju', userId?: string) => {
		if (bool && userId) {
			const res = await userAPI.fetchUserDataAPI(userId);
			setPopupData(res);
			if (type === 'balju') {
				setShowBaljuUserPopup(true);
			} else {
				setShowSujuUserPopup(true);
			}
		} else {
			setPopupData(undefined);
			if (type === 'balju') {
				setShowBaljuUserPopup(false);
			} else {
				setShowSujuUserPopup(false);
			}
		}
	};

	const onClosePopup = async (e: any, type: 'balju' | 'suju', userId?: string) => {
		e.preventDefault();
		if (!userId) return;
		if (type === 'balju') {
			setShowBaljuUserPopup(false);
		} else {
			setShowSujuUserPopup(false);
		}
		setPopupData(undefined);
	};

	const items = !baechaData ? [] : [
		{
			key: `${baechaData.id}_baljuGroupName`,
			span: 1,
			label: '배차방',
			children: baechaData.baljuGroupId === baechaData.baljuingGroupId
				? baechaData.baljuGroupName
				: <pre>{`${baechaData.baljuingGroupName}\n← ${baechaData.baljuGroupName}`}</pre>
		},
		{
			key: `${baechaData.id}_carType`,
			span: 1,
			label: '차량',
			children: baechaData.carType,
		},
		{
			key: `${baechaData.id}_status`,
			span: 1,
			label: '상태',
			children: (
				<Tag color={getStatusColor(baechaData.status)}>
					{getBaechaStatusStr(baechaData.status)}
				</Tag>
			),
		},
		{
			key: `${baechaData.id}_workArriveDate`,
			span: 3,
			label: '작업 도착일',
			children: `${formatKorDate(baechaData.workArriveDate)} ${dayjs(baechaData.workArriveDate).format('HH:mm')}`,
		},
		{
			key: `${baechaData.id}_workContent`,
			span: 1,
			label: '작업 내용',
			children: baechaData.workContent,
		},
		{
			key: `${baechaData.id}_workEtc`,
			span: 2,
			label: '전달 및 특이사항',
			children: baechaData.workEtc,
		},
		{
			key: `${baechaData.id}_privateAddress`,
			span: 1,
			label: '(수주자에게만 공개)현장 주소',
			children: baechaData.privateAddress,
		},
		{
			key: `${baechaData.id}_privatePhone`,
			span: 1,
			label: '(수주자에게만 공개)현장 연락처',
			children: baechaData.privatePhone,
		},
		{
			key: `${baechaData.id}_privateNote`,
			span: 1,
			label: '(수주자에게만 공개)기타',
			children: baechaData.privateNote,
		},
		{
			key: `${baechaData.id}_workLor`,
			span: 3,
			label: '주소',
			children: baechaData.workLoc,
		},
		{
			key: `${baechaData.id}_workDay`,
			span: 1,
			label: 'workDay',
			children: baechaData.workDay,
		},
		{
			key: `${baechaData.id}_workHour`,
			span: 1,
			label: 'workHour',
			children: baechaData.workHour,
		},
		{
			key: `${baechaData.id}_workDayCount`,
			span: 1,
			label: 'workDayCount',
			children: baechaData.workDayCount,
		},

		{
			key: `${baechaData.id}_priceType`,
			span: 1,
			label: '단가 유형',
			children: getBaechaPriceTypeStr(baechaData.priceType),
		},
		{
			key: `${baechaData.id}_price`,
			span: 1,
			label: '총액',
			children: formatCurrency(Number(baechaData.price), 'KRW'),
		},
		{
			key: `${baechaData.id}_priceReceiveMethod`,
			span: 1,
			label: '결제',
			children: getBaechaPriceReceiveMethodStr(baechaData.priceReceiveMethod),
		},
		{
			key: `${baechaData.id}_fee`,
			span: 1,
			label: '수수료',
			children: formatCurrency(Number(fee), 'KRW'),
		},
		{
			key: `${baechaData.id}_baljuPoint`,
			span: 1,
			label: '발주 점수',
			children: baechaData.baljuPoint,
		},
		{
			key: `${baechaData.id}_sujuPoint`,
			span: 1,
			label: '수주 점수',
			children: baechaData.sujuPoint ? `-${baechaData.sujuPoint}` : 0,
		},
		{
			key: `${baechaData.id}_baljuMemberNickname`,
			span: 1,
			label: '발주자',
			children: (
				<Popover
					placement="top"
					content={(
						// eslint-disable-next-line jsx-a11y/anchor-is-valid
						<a onClick={(e: any) => onClosePopup(e, 'balju', baechaData?.baljuUserId || '')}>
							{popupData?.memberInfo?.filter((item: any) => item.status === 'ok').length > 0
								&& (
									<div>
										{popupData?.memberInfo?.filter((item: any) => item.status === 'ok')
											.map((item: any) => <div key={item.name}>{item.name}</div>)}
									</div>
								)}
						</a>
					)}
					trigger="click"
					title={(
						<div style={{ display: 'flex', justifyContent: 'space-between' }}>
							<div>{popupData?.userInfo?.name || ''}</div>
							<div>{popupData?.userInfo?.phoneNum || ''}</div>
						</div>
					)}
					open={showBaljuUserPopup}
					onOpenChange={async (bool) => onOpenPopup(bool, 'balju', baechaData?.baljuUserId)}
				>
					<div className={styles.clickable}>
						<div>{baechaData.baljuMemberNickname}</div>
						<div>{formatPhoneNumber(baechaData.baljuUserPhoneNum || '')}</div>
					</div>
				</Popover>
			),
		},
		{
			key: `${baechaData.id}_sujuMemberNickname`,
			span: 1,
			label: '수주자',
			children: (
				<Popover
					placement="top"
					content={(
						// eslint-disable-next-line jsx-a11y/anchor-is-valid
						<a onClick={(e: any) => onClosePopup(e, 'suju', baechaData?.sujuUserId || '')} >
							{popupData?.memberInfo?.filter((item: any) => item.status === 'ok').length > 0
								&& (
									<div>
										{popupData?.memberInfo?.filter((item: any) => item.status === 'ok')
											.map((item: any) => <div key={item.name}>{item.name}</div>)}
									</div>
								)}
						</a>
					)}
					title={(
						<div style={{ display: 'flex', justifyContent: 'space-between' }}>
							<div>{popupData?.userInfo?.name || ''}</div>
							<div>{popupData?.userInfo?.phoneNum || ''}</div>
						</div>
					)}
					trigger="click"
					open={showSujuUserPopup}
					onOpenChange={async (bool) => onOpenPopup(bool, 'suju', baechaData?.sujuUserId || '')}
				>
					<div className={styles.clickable}>
						<div>{baechaData.sujuMemberNickname}</div>
						<div>{formatPhoneNumber(baechaData.sujuUserPhoneNum || '')}</div>
					</div>
				</Popover>
			),
		},
		{
			key: `${baechaData.id}_sujuCarNumber`,
			span: 1,
			label: '작업차량',
			children: baechaData.sujuCarNumber,
		},
		{
			key: `${baechaData.id}_baechaWorkDayData`,
			span: 3,
			label: '작업 상세',
			children: (
				<BaechaWorkDayDetail
					baechaData={baechaData}
					baechaWorkDayData={baechaWorkDayData}
					baechaUpdateHistoryData={baechaUpdateHistoryData}
					onClickBaechaWorkDay={onClickBaechaWorkDayItem}
					onClickAddButton={onClickAddButton}
				/>
			)
		},
		{
			key: `${baechaData.id}_callPassData`,
			span: 3,
			label: '콜/패스',
			children: <CallPassTable baechaData={baechaData} callPassData={callPassData} />,
		},
	];

	return (
		<Descriptions
			className={styles.description}
			bordered
			layout="horizontal"
			column={3}
			size="small"
			items={items}
			labelStyle={{ textAlign: 'center' }}
		/>
	);
}